import { useCurrentWorkspace } from '@/context/current-workspace';
import { hydraUiApi } from '@/services/api';
import { useCallback, useMemo } from 'react';
import { postCatalogEvent } from './event-name';
import { type IEvents, type EventProperties } from './types';
import { useIsAnalyticsEnabled } from './use-is-analytics-enabled';

export { trackEventForAccount, useAccountAnalytics } from './track-account-event';
export { useIsAnalyticsEnabled } from './use-is-analytics-enabled';

/**
 * Post a typed analytics event to the environment-scoped tracking endpoint
 * given the environment id directly, with no React context. `useAnalytics` reads
 * the environment from the workspace context, but callers mounted outside that
 * context (e.g. the reauth modal, which the modal-manager renders at the app
 * shell) can't — they thread the last-good environment id in and call this.
 * Best-effort: resolves on success or failure so it never blocks or surfaces.
 */
export function trackEventForEnvironment<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace] & string,
  Properties extends Record<EventProperties<Namespace, E>, unknown>,
>(environmentId: string, namespace: Namespace, event: E, properties: Properties): Promise<void> {
  return postCatalogEvent(namespace, event, properties, (body) =>
    hydraUiApi.tracking.createTrackEventForEnvironment({ params: { path: { environment_id: environmentId } }, body }),
  );
}

export function useAnalytics() {
  const workspace = useCurrentWorkspace();
  const isEnabled = useIsAnalyticsEnabled();

  const track = useCallback(
    <
      Namespace extends keyof IEvents,
      E extends keyof IEvents[Namespace] & string,
      Properties extends Record<EventProperties<Namespace, E>, unknown>,
    >(
      namespace: Namespace,
      event: E,
      properties: Properties,
    ) => {
      if (isEnabled === false || workspace == null) {
        return Promise.resolve();
      }
      return trackEventForEnvironment(String(workspace.id), namespace, event, properties);
    },
    [isEnabled, workspace],
  );

  return useMemo(() => ({ track, isEnabled }), [track, isEnabled]);
}
